import {elementFromString} from '@tiptap/core';
import {DOMParser as ProseMirrorDOMParser, Fragment, Mark, Node as ProseMirrorNode, Schema} from '@tiptap/pm/model';
import type {HtmlAttributes} from './rich-text-schema';

/**
 * Writes the editor's document as HTML, with every attribute exactly as it was loaded or set.
 * ProseMirror's own serializer sets `style` through the style object, which re-spells it
 * (`color:#e74c3c;` comes out as `color: rgb(231, 76, 60);`), and stored texts must come back as
 * they were. Text and attribute values are escaped as the browser's innerHTML escapes them
 * (`&amp;`, `&lt;`, `&gt;`, `&nbsp;`, and `&quot;` in attributes); void elements are written
 * `<br>`, `<img ...>`, `<hr>`.
 *
 * Marks open and close as ProseMirror's serializer does it: a run of nodes keeps the marks it
 * shares with the node before it open, outermost first.
 */
export const serializeHtml = (content: Fragment): string => {
  let html = '';
  content.forEach((node) => {
    html += node.isTextblock ? block(node) : blockOrNode(node);
  });
  return html;
};

const escapeText = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/ /g, '&nbsp;');

const escapeAttribute = (value: string): string =>
  String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/ /g, '&nbsp;');

const openTag = (tag: string, attributes: HtmlAttributes = {}): string =>
  `<${tag}${Object.entries(attributes).map(([name, value]) => ` ${name}="${escapeAttribute(value)}"`).join('')}>`;

const BLOCK_TAGS: Record<string, string> = {
  paragraph: 'p', bulletList: 'ul', orderedList: 'ol', listItem: 'li', blockquote: 'blockquote',
};

const tagOf = (node: ProseMirrorNode): string =>
  node.type.name === 'heading' ? `h${node.attrs['level'] || 1}` : BLOCK_TAGS[node.type.name] || 'div';

const blockOrNode = (node: ProseMirrorNode): string => {
  if (node.type.name === 'horizontalRule') {
    return openTag('hr', node.attrs['htmlAttributes']);
  }
  if (node.isTextblock) {
    return block(node);
  }
  let inner = '';
  node.forEach((child) => {
    inner += blockOrNode(child);
  });
  return `${openTag(tagOf(node), node.attrs['htmlAttributes'])}${inner}</${tagOf(node)}>`;
};

const block = (node: ProseMirrorNode): string =>
  `${openTag(tagOf(node), node.attrs['htmlAttributes'])}${inline(node.content)}</${tagOf(node)}>`;

const markTag = (mark: Mark): string => mark.attrs['tag'];

/** A run of inline nodes with their marks. */
export const inline = (content: Fragment): string => {
  let html = '';
  const open: Mark[] = [];
  const closeTo = (depth: number) => {
    while (open.length > depth) {
      html += `</${markTag(open.pop())}>`;
    }
  };
  content.forEach((node) => {
    const marks = node.marks.filter((mark) => mark.type.name === 'htmlInline');
    let keep = 0;
    while (keep < open.length && keep < marks.length && open[keep].eq(marks[keep])) {
      keep++;
    }
    closeTo(keep);
    for (const mark of marks.slice(keep)) {
      html += openTag(markTag(mark), mark.attrs['attributes']);
      open.push(mark);
    }
    if (node.isText) {
      html += escapeText(node.text);
    } else if (node.type.name === 'hardBreak') {
      html += '<br>';
    } else if (node.type.name === 'htmlImage') {
      html += openTag('img', node.attrs['attributes']);
    }
  });
  closeTo(0);
  return html;
};

/** The HTML as the editor would hold it: parsed into its schema and written back. */
export const throughSchema = (html: string, schema: Schema): string => {
  const document = ProseMirrorDOMParser.fromSchema(schema).parse(elementFromString(html || ''));
  return document.isTextblock ? inline(document.content) : serializeHtml(document.content);
};
